'use client';
/**
 * Search page
 *
 * This page accepts two different query parameters
 * query - this should be the search query, in plain text. ?query=torten
 *    for example. This is a required
 * category - an optional parameter to pre-select the category selected for
 *    searching.  &category=articles for example. If used, it should be one
 *    of the following: all, recipes, products, tips, articles
 *
 * Results sorting is done by changing the algolia index used. There are indexes
 * configured for each sorting type, for each data type.
 *
 * Filtering is done via configured Algolia Facets.  The facets are loaded whenever
 * a new data type is selected and converted into the available filters. Outside of
 * picking which facets to display, the filtering is done completely through algolia.
 *
 */
import { usePrevious } from '../../hooks/usePrevious';
import { ANY_SORT_METHODS, SORT_METHODS, SortItemType } from '../../hooks/useSort';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import type { FilterTopic } from 'components/FilterMenu/types';
import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import FilterMenu from './(search)/FilterMenu';
import NoResultsMessage from './(search)/NoResultsMessage';
import SearchCategorySelector from './(search)/SearchCategorySelector';
import SortAndFilter from './(search)/SortAndFilter';
import { TrackingService } from 'service/tracking/trackingService';
import { useGlobalContext } from 'components/Context/hooks/useGlobalContext';

// after search refactoring, everything algolia related will be moved to the server/oe api
import type { SearchResultCard } from 'server/repository/algolia/mapper';
import { getSearchResults } from 'server/repository/algolia/requests/getSearchResults';
import getSearchClient from 'server/repository/algolia/client';

interface ParsedUrlQueryForPage extends ParsedUrlQuery {
  query?: string;
  category?: string;
  filters?: string;
}

const SearchResultsGrid = dynamic(
  () => import('components/SearchResultsGrid').then((m: any) => m.SearchResultsGrid),
  { ssr: true },
) as any;

const SEARCH_PAGE_SIZE = 12;

const ClientSearchPage = ({
  locale,
  recipeEnv,
  searchItemLabels,
  searchPageLabels,
}: {
  locale: string;
  recipeEnv: string;
  searchItemLabels: Record<string, string>;
  searchPageLabels: Record<string, string>;
}) => {
  const { pathTranslations } = useGlobalContext();
  const router = useRouter();
  const { query = '', category = '', filters = '' } = router.query as ParsedUrlQueryForPage;

  const searchCategories: Record<string, string> = {
    ALL: searchItemLabels?.searchCategoryAll || 'All',
    RECIPES: searchItemLabels?.searchCategoryRecipes || 'Recipes',
    PRODUCTS: searchItemLabels.searchCategoryProducts || 'Products',
    TIPS: searchItemLabels?.searchCategoryTips || 'Tips & Tricks',
    ARTICLES: searchItemLabels.searchCategoryArticles || 'Articles',
  };

  // en_ZA is the only market which doesn't contain recipes and tips
  if (locale === 'en_ZA') {
    delete searchCategories.RECIPES;
    delete searchCategories.TIPS;
  }

  const [queryDone, setQueryDone] = useState<boolean>(false);
  const [totalSearchResult, setTotalSearchResult] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [searchResultsCards, setSearchResultsCards] = useState<SearchResultCard[]>([]);
  const [isSearchComplete, setSearchComplete] = useState<boolean>(false);
  const [sortItemType, setSortItemType] = useState<SortItemType>(SortItemType.ANY);
  const [currentSearchCategory, setCurrentSearchCategory] = useState<string>('ALL');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [availableFilters, setAvailableFilters] = useState<FilterTopic[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<string[][]>([]);
  const [currentSort, setCurrentSort] = useState<string>(ANY_SORT_METHODS.RELEVENCE);

  const previousAppliedFilters = usePrevious(appliedFilters);
  const previousSearchCategory = usePrevious(currentSearchCategory);
  const previousSort = usePrevious(currentSort);

  useEffect(() => {
    TrackingService.contentGroups({
      cg1: 'search',
    });

    if (category) {
      const searchCategoriesKeys = Object.keys(searchCategories);
      const catKey = category.toUpperCase();
      setCurrentSearchCategory(
        searchCategoriesKeys.includes(catKey) ? catKey : searchCategoriesKeys[0],
      );
    }

    setAppliedFilters(() => {
      if (!filters) return [];
      return [filters.split(',')];
    });
  }, []);

  // On load search tracking data
  let finalFilterCategoryText = '';
  let finalFilterValueText = '';
  const finaldata: any = [];
  const appliedFilterCategory = appliedFilters.map((item) => {
    const filterCategoryData = item[0].split(':')[0];
    return filterCategoryData;
  });
  const appliedFiltersCategory = appliedFilters.map((item) => {
    const filterCategoryValue = item.map((category) => category.split(':')[1]);
    filterCategoryValue.forEach((data) => {
      finaldata.push(data);
    });
    return finaldata;
  });
  appliedFilterCategory.forEach((category: string) => {
    finalFilterCategoryText += `${category},`;
  });
  appliedFiltersCategory[0]?.forEach((items: string) => {
    finalFilterValueText += `${items},`;
  });

  const handleSetAvailableFilters = (availableFilters: FilterTopic[]) => {
    setAvailableFilters(availableFilters);
  };

  const searchResult = async (loadFacets: boolean, sortMethod: string) => {
    try {
      const searchClient = getSearchClient();

      const { totalResults, searchResults } = await getSearchResults({
        pathTranslations,
        query,
        sortMethod,
        locale,
        recipeEnv,
        searchClient,
        SEARCH_PAGE_SIZE,
        page,
        searchPageLabels,
        loadFacets,
        onSetAvailableFilters: handleSetAvailableFilters,
        currentSearchCategory,
        searchCategories,
        searchItemLabels,
        appliedFilters,
      });

      setTotalSearchResult(totalResults);
      setSearchResultsCards(page === 0 ? searchResults : [...searchResultsCards, ...searchResults]);
      setQueryDone(true);
    } catch (err) {
      setTotalSearchResult(0);
      setSearchResultsCards([]);
      console.error('Failed to fetch recipes', err);
    }
  };

  useEffect(() => {
    // Reset state when we change things that affect pagenation - category, filters, sort
    const loadFacets =
      currentSearchCategory !== previousSearchCategory || appliedFilters !== previousAppliedFilters;

    let sortMethod: string = currentSort;
    if (
      currentSearchCategory !== previousSearchCategory ||
      appliedFilters !== previousAppliedFilters ||
      currentSort !== previousSort
    ) {
      setPage(0);
    }
    if (currentSearchCategory !== previousSearchCategory) {
      sortMethod = SORT_METHODS.RELEVENCE;
      setCurrentSort(sortMethod);
    }

    searchResult(loadFacets, sortMethod);

    TrackingService.searchChange(query, finalFilterCategoryText, finalFilterValueText, currentSort);
  }, [page, query, currentSearchCategory, currentSort, appliedFilters]);

  useEffect(() => {
    // timeout to render cards content, otherwise we will see "no results" message for miliseconds
    setTimeout(() => {
      setSearchComplete(true);
    }, 300);
  }, [searchResultsCards, queryDone]);

  const selectedFilters = availableFilters.filter((filter) =>
    filter.options.some((option) => option.selected),
  );

  const searchResultTrackingContainer = (data: any, position?: number) => {
    TrackingService.searchResult(
      'na', // no category data exists at the card level, only subcategory
      data.category || 'na', // category in the data object is actually subcategory for recipes
      data.title || 'na',
      currentSearchCategory || 'na',
      position || 0,
      finalFilterCategoryText || 'na',
      finalFilterValueText || 'na',
      currentSort || 'na',
    );
  };

  const handleCategorySelect = (category: string, sortType: SortItemType) => {
    setPage(0);
    setAppliedFilters([]);
    setCurrentSearchCategory(category);
    setSortItemType(sortType);
  };

  const handleApplyFilters = (filters: string[][]) => {
    setAppliedFilters(filters);
    setQueryDone(false);
  };

  const handleSort = (method: string) => {
    setCurrentSort(method);
    setQueryDone(false);
  };

  const handleFilter = () => {
    setShowFilters(true);
  };

  const handleFilterClose = () => {
    setShowFilters(false);
  };

  return (
    <div className="min-h-screen">
      <FilterMenu
        isOpen={showFilters}
        onClose={handleFilterClose}
        totalSearchResult={totalSearchResult}
        availableFilters={availableFilters}
        onApplyFilters={handleApplyFilters}
        searchItemLabels={searchItemLabels}
        currentSearchCategory={currentSearchCategory}
        query={query}
      />
      {queryDone && (
        <div className="mt-10">
          <SearchResultsGrid
            id="search-results"
            heading={
              query && searchPageLabels?.your_results_for
                ? `${searchPageLabels?.your_results_for} "${query}"`
                : `${searchPageLabels?.your_results || ''}`
            }
            subHeading={searchPageLabels?.search_subheading}
            cardData={searchResultsCards}
            labels={searchItemLabels}
            navContentLeft={
              <SearchCategorySelector
                onSelect={handleCategorySelect}
                searchCategories={searchCategories}
                currentSearchCategory={currentSearchCategory}
                query={query}
              />
            }
            navContentRight={
              <SortAndFilter
                selectedFilters={selectedFilters}
                sortItemType={sortItemType}
                searchItemLabels={searchItemLabels}
                currentSort={currentSort}
                totalSearchResult={totalSearchResult}
                currentSearchCategory={currentSearchCategory}
                onSort={handleSort}
                onFilter={handleFilter}
              />
            }
            visibleCount={searchResultsCards?.length || 0}
            totalCount={totalSearchResult}
            onShowMore={() => setPage(page + 1)}
            // prettier-ignore
            noResultsMessage={(
              <NoResultsMessage
                header={searchPageLabels?.no_results_header || 'Uh Oh.'}
                message={searchPageLabels?.no_results_subtitle || 'Sorry we found no results matching your query'}
                helpMessage={searchPageLabels?.no_results_help}
              />
            )}
            trackingEvent={searchResultTrackingContainer}
            isSearchComplete={isSearchComplete}
          />
        </div>
      )}
    </div>
  );
};

export default ClientSearchPage;
